/**
 * /admin/pitch-kit — One-stop ready-made pitches for talking to:
 *   - Lawyers
 *   - Tech people
 *   - Insurance companies
 *   - Anyone (the short / medium / long bios)
 *
 * Goal: make these talking points easy to find on the fly (admin
 * sidebar, mobile-friendly), copy with one tap, or download as a PDF
 * to email to someone.
 *
 * No backend needed — content is static markdown rendered through
 * simple tags. Update copy by editing PITCH_CONTENT below.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, CopyIcon as Copy, DownloadSimpleIcon as DownloadSimple, BookOpenIcon as BookOpen, BriefcaseIcon as Briefcase, CodeIcon as Code, ShieldCheckIcon as ShieldCheck, ChatCircleTextIcon as ChatCircleText, CheckIcon as Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PITCH_CONTENT = {
  bios: {
    label: "Short bios",
    icon: ChatCircleText,
    blurb: "Three lengths for any conversation — pick one based on how much time you have.",
    sections: [
      {
        heading: "10 seconds (elevator door closing)",
        text: `I built Armstrong K9 Registry — it's the trusted place to register, buy, sell, and care for purebred and exotic dogs, with verified breeders, escrow-backed payments, and a digital diary for the dog you already have.`,
      },
      {
        heading: "30 seconds (coffee shop intro)",
        text: `You know how buying or selling a dog online is full of scams, fake pedigrees, and no recourse when something goes wrong? I built Armstrong K9 Registry to fix that.

Every breeder is verified. Every transaction runs through Stripe-backed escrow — the money only releases when the dog is safely transferred. Buyers can do a live video tour before they pay a dollar, and a vetted network of transporters handles the trip.

And for people who already have a dog they love? We built a daily care diary — meals, walks, weight, vaccinations, vet visits, training — with smart warnings that flag symptoms before they become emergencies.

One platform. Four audiences: breeders, buyers, transporters, and the everyday dog owner. Built in Northern California. Independent of legacy registries. ArmstrongK9Registry.com.`,
      },
      {
        heading: "60 seconds (when they ask 'tell me more')",
        text: `There's no good place online to buy or sell a purebred or exotic dog with any kind of safety net. Most marketplaces are just classified ads — no identity checks on the seller, no escrow, no recourse when the puppy doesn't show up, or shows up sick, or isn't who they said it was. Even the legacy paper registries are stuck in the 1980s.

Armstrong K9 Registry is the first platform built end-to-end around trust. Every kennel is identity-verified. Every dog gets a 4-generation digital pedigree with automatic inbreeding warnings. Every transaction is held in escrow until the dog is safely delivered. Buyers can take a live video tour of the kennel before they commit. A vetted transporter network handles the trip with the same escrow protection.

But what surprises people is that we're not just for breeders and buyers. We built a daily care diary called Dog Log for the everyday dog owner — meals, walks, weight, weather, training, medication, behavior, ten categories total — with smart warnings that catch repeating symptoms before they become emergencies. There's a calendar that auto-tracks heat cycles for breeders, predicted whelp dates, vet appointments, and birthdays. There's a one-tap sitter portal for when you travel.

We serve four audiences on one platform: serious breeders, prospective buyers, dog transporters, and the everyday dog owner who just loves the dog they already have. Built in Northern California. Independent of legacy registries. We launched in May 2026.`,
      },
    ],
  },

  lawyer: {
    label: "Lawyer",
    icon: Briefcase,
    blurb: "Frame the platform as a multi-sided marketplace + SaaS. Their job is to map liability, contracts, and compliance.",
    sections: [
      {
        heading: "Lead with this",
        text: `Armstrong K9 Registry is a multi-sided marketplace + SaaS platform. We're a payment facilitator using Stripe Connect — we never touch funds directly — and we operate an escrow model between buyers, breeders, and transporters. We're independent of legacy registries like AKC, which means our certificates have brand value, not statutory force.`,
      },
      {
        heading: "Multi-sided marketplace liability",
        text: `4 user types: breeders (sellers), buyers, transporters (logistics contractors), owners (consumers). We act as the intermediary — never the seller of record for the dogs themselves. Section 230 exposure: we host user content (kennel pages, listings, reviews) — important for content-moderation framing. Live animals = uniquely complex commodity (state-by-state Lemon Laws, USDA APHIS, state consumer-protection statutes for live-animal sales).`,
      },
      {
        heading: "Payment / escrow architecture",
        text: `Stripe-backed escrow: buyer funds locked → released on confirmed dog transfer. Stripe Connect for transporter payouts (KYC handled by Stripe). Platform fees: percentage-based on transactions (admin-configurable via pricing console). Critical question for them: Does our escrow flow qualify us as a Money Service Business under FinCEN? (Probably no — Stripe handles the regulated layer — but get the opinion in writing.)`,
      },
      {
        heading: "Predictive-features liability (high-priority)",
        text: `We display estimated whelp dates (breeding + 63 days) and predicted next-heat dates (last + 180 days). Terms §5e disclaims warranty + advises veterinary confirmation. Risk you want their take on: a buyer who books non-refundable travel based on the predicted whelp date and the puppies arrive 5 days late — are we liable for reliance damages? Our disclaimer covers it, but get the opinion on jurisdiction-specific enforceability.`,
      },
      {
        heading: "AI-generated content (Anthropic / Claude via Emergent LLM Key)",
        text: `Marketing pack generator + kennel-bio assistant. Disclosed in Privacy §2.9, ToS §5f, Sub-processors page. No training on user content per provider terms. Risk question: who owns AI output? Generally the user, but courts are unsettled.`,
      },
      {
        heading: "Data + privacy",
        text: `GDPR (EU-eligible users), CCPA (CA operator + CA users). Sensitive personal data: reproductive-health calendar entries (heat cycles, breedings) — may trigger heightened protections under CCPA "sensitive PI" or state biometric/health data laws. Sub-processors fully disclosed: Stripe, Resend, MongoDB Atlas, Anthropic, Apple Push, FCM, Mozilla autopush, Cloudflare Turnstile, Daily.co.`,
      },
      {
        heading: "Trust + safety architecture",
        text: `Identity verification on breeders (Stripe Identity). 2FA + Cloudflare Turnstile. Microchip uniqueness check at dog registration (no dog can be registered twice). Inbreeding coefficient (COI) warnings — argued as a "consumer protection" feature.`,
      },
      {
        heading: "Contract surfaces they need to review",
        text: `Terms of Service · Privacy Policy · Storefront Seller Agreement (Dog Shop) · Refund Policy · Ethical Considerations · Acceptable Use. MISSING and worth flagging: a formal Transporter Independent Contractor Agreement and a Buyer Purchase Agreement template that flows through escrow.`,
      },
      {
        heading: "Questions to ask them",
        text: `1. What's our exposure on live-animal sales across the 50 states — should we restrict shipping into certain states?
2. Do we need a Bond or specific insurance for the escrow model, or does Stripe's licensing cover us?
3. Are our predictive-feature disclaimers enforceable in CA, FL, TX?
4. Do we have any USDA APHIS exposure given that we facilitate transactions but don't transport or house dogs?`,
      },
    ],
  },

  tech: {
    label: "Tech",
    icon: Code,
    blurb: "Establish you've built a real, defensible system — not a no-code marketplace.",
    sections: [
      {
        heading: "Lead with this",
        text: `Armstrong K9 Registry is a full-stack platform — React/Tailwind frontend, FastAPI/MongoDB backend — with persona-aware data flows, Stripe Connect escrow, integrated Anthropic Claude generation, and a CI/CD workflow that includes off-pod GitHub Actions for memory-intensive jobs.`,
      },
      {
        heading: "Stack",
        text: `Frontend: React + Tailwind + Shadcn UI + Phosphor icons, Spectral serif / Manrope sans.
Backend: FastAPI (Python 3.11) + MongoDB (Motor async driver).
Auth: JWT-based custom auth + Google OAuth (Emergent-managed) + 2FA via PyOTP.
Payments: Stripe Connect (escrow + payouts), Stripe Identity (KYC).
AI: Anthropic Claude 3.5 Sonnet via Emergent LLM Key (single key, multi-provider).
Infrastructure: Containerized, supervisor-managed, Kubernetes ingress on Emergent platform.
Real-time: Web Push (VAPID + PyWebPush), persona-aware notification routing.
Video: Daily.co for live tours, Playwright/Chromium for marketing-clip generation (offloaded to GitHub Actions).
Anti-abuse: Cloudflare Turnstile on public forms.`,
      },
      {
        heading: "Non-trivial systems",
        text: `• Pedigree DAG with COI inbreeding calculation at registration time
• Dynamic pricing & platform-fee overrides that intercept Stripe price caching and regenerate Products/Prices on the fly
• Persona-aware calendar aggregator pulling from 6 collections (dogs, litters, transports, video tours, heats, custom events) into a unified timeline with idempotent web-push dedup
• AI marketing generator with 7 swappable lead angles, audience-specific email rules, revenue suppression below a threshold, json_repair fallback
• GitHub Actions HMAC pipeline for off-pod heavy work (Playwright video recording) — HMAC-SHA256 over timestamp.body_sha256, idempotent uploads, constant-time signature compare
• Background loops in lifespan: care_weekly_recap, calendar_reminder, automation reverify, transport cleanups`,
      },
      {
        heading: "Data architecture",
        text: `30+ collections in MongoDB. All IDs are UUIDs (not ObjectIds in API responses). All timestamps are ISO 8601 UTC strings. Sensitive flows always exclude _id from projections. Soft deletes with audit trails on critical entities (dogs, litters, transactions).`,
      },
      {
        heading: "DevOps",
        text: `Preview vs Production environment separation. Secrets via .env. GitHub Actions for off-pod work (frees the 512MB production pod). Stripe webhooks signed + idempotency-keyed. Background loops wrapped in try/except so a crash doesn't kill the loop.`,
      },
      {
        heading: "AI integration depth",
        text: `Single EMERGENT_LLM_KEY routes to Anthropic / OpenAI / Gemini. Background-job/poll pattern for long generations (bypasses 60s ingress timeout). Idempotent generation tracking via marketing_jobs collection. Hard-constraint prompt engineering (no AKC, no emoji, no invented stats), geography baked in (Northern California, ArmstrongK9Registry.com), audience-first email rules.`,
      },
      {
        heading: "Be ready for these challenges",
        text: `• Test coverage: backend has known flakes in Motor event-loop singletons (P2 backlog)
• Single-instance database: at scale you'll want MongoDB Atlas replica sets / sharding
• Monolithic backend: transport.py is ~3,000 lines (refactor on the backlog)
• No SLA on push delivery — Apple/Google/Mozilla services are best-effort (disclosed in legal docs)
• AI dependency: marketing generator depends on Claude availability (graceful degrade exists)`,
      },
      {
        heading: "What to emphasize",
        text: `Persona-first architecture, not just UI. Legal-first mindset (full sub-processor disclosure, web push opt-in, AI training disclaimer). Real iteration cadence with screenshot/curl/Playwright test agents. Moat is integration depth — pedigree + escrow + transport + care diary + AI on one stack, not 5 different SaaS tools wired together.`,
      },
    ],
  },

  insurance: {
    label: "Insurance",
    icon: ShieldCheck,
    blurb: "Frame yourself as TECHNOLOGY, not as a dog seller / breeder / transporter. This single framing matters the most.",
    sections: [
      {
        heading: "Lead with this exact sentence (put it at the top of every email)",
        text: `Armstrong K9 Registry is a technology platform / marketplace operator headquartered in Northern California. We do not own, breed, transport, or take possession of any dogs. We facilitate transactions and provide software tools. Our payment processing is done through Stripe (we are not a money services business). Our user base spans the United States, primarily in the dog-breeding and pet-ownership categories.`,
      },
      {
        heading: "Policies to ask about",
        text: `1. Technology Errors & Omissions (Tech E&O) — most important. Covers claims that your software didn't perform as promised. Specifically ask about coverage for AI-generated content, predictive features (whelp dates), platform availability. Target limit for early-stage SaaS: $1M–$2M aggregate.

2. Cyber Liability — covers data breaches, ransomware, GDPR/CCPA regulatory fines, notification costs. Stripe handles your PCI scope — TELL THEM THIS; it lowers your premium.

3. General Liability (GL) — standard, probably won't cost much; no physical premises serving customers.

4. Media Liability / Defamation — you host user content (kennel pages, listings, reviews). Often bundled with Tech E&O.

5. Directors & Officers (D&O) — not needed until you raise outside money.

6. Animal-specific exclusions — push back on these. Most tech E&O policies have animal exclusions. You need to negotiate around them or get a rider, because the entire domain is animals.`,
      },
      {
        heading: "What you DO (state clearly)",
        text: `• Operate a website + mobile-responsive web app
• Host kennel pages, listings, reviews (user-generated)
• Facilitate payments via Stripe (escrow model)
• Send transactional emails via Resend
• Send web push notifications via Apple/Google/Mozilla services
• Generate AI marketing copy via Anthropic Claude (admin-only)
• Display predicted dates (whelp, heat) with explicit "estimate only" disclaimers
• Operate a calendar/reminder system
• Vet transporters before allowing them into the dispatch board`,
      },
      {
        heading: "What you DO NOT DO (just as important)",
        text: `• Sell or own dogs
• Physically transport dogs
• Provide veterinary advice or services
• House, board, or care for dogs
• Issue legally-binding pedigrees (your registry is independent — brand value, not statutory)
• Process payments directly (Stripe is the processor)
• Collect health records subject to HIPAA (animal data isn't covered)
• Operate in regulated industries beyond what Stripe handles`,
      },
      {
        heading: "Risk-reducing facts to emphasize",
        text: `Stripe handles payment regulation — not PCI-DSS scope, not a Money Service Business. Identity verification on breeders before they can list (Stripe Identity). Vetted transporters — not anyone can be a transporter. Multi-factor authentication available on all accounts. Cloudflare Turnstile bot protection on public forms. Full legal disclosures: Terms of Service, Privacy Policy, Sub-processors, User Consent, Ethical Considerations. Predictive features carry explicit disclaimers. All AI-generated content reviewed by admin before publication. Web push is opt-in only.`,
      },
      {
        heading: "Questions to ask them",
        text: `• Do you have a SaaS-marketplace product, or am I getting a generic small-business policy?
• How do you handle animal-related exclusions in tech E&O? Will you write a rider?
• What's the typical premium for a Stage 1 SaaS doing $X in revenue with $1M E&O + $1M cyber?
• If a buyer claims my predicted whelp date misled them and they lost a non-refundable flight booking, is that covered or excluded?
• What if a transporter is in an accident with a dog they're transporting — is that my exposure or theirs?
• Do you cover AI-output liability? (e.g., AI-generated marketing copy contains a factual error that misleads a customer)`,
      },
    ],
  },
};

export default function AdminPitchKit() {
  const [active, setActive] = useState("bios");

  const copyAll = (audience) => {
    const data = PITCH_CONTENT[audience];
    const out = [`# ${data.label}`, "", data.blurb, "", ...data.sections.flatMap((s) => [`## ${s.heading}`, "", s.text, ""])].join("\n");
    navigator.clipboard.writeText(out).then(
      () => toast.success("Copied to clipboard."),
      () => toast.error("Clipboard blocked — try the download button."),
    );
  };

  const downloadAll = (audience) => {
    const data = PITCH_CONTENT[audience];
    const out = [`Armstrong K9 Registry — ${data.label} pitch`, "=".repeat(60), "", data.blurb, "", ...data.sections.flatMap((s) => [s.heading, "-".repeat(s.heading.length), "", s.text, ""])].join("\n");
    const blob = new Blob([out], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `armstrong-pitch-${audience}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded armstrong-pitch-${audience}.txt`);
  };

  const downloadEverything = () => {
    const sections = ["bios", "lawyer", "tech", "insurance"];
    const out = [
      "Armstrong K9 Registry — Complete Pitch Kit",
      "=".repeat(60),
      `Generated: ${new Date().toLocaleString()}`,
      "",
      ...sections.flatMap((k) => {
        const d = PITCH_CONTENT[k];
        return [
          "",
          "=".repeat(60),
          d.label.toUpperCase(),
          "=".repeat(60),
          d.blurb,
          "",
          ...d.sections.flatMap((s) => [s.heading, "-".repeat(s.heading.length), "", s.text, ""]),
        ];
      }),
    ].join("\n");
    const blob = new Blob([out], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `armstrong-pitch-kit-complete.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded complete pitch kit.");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-pitch-kit-page">
      <Link to="/admin" className="text-sm text-[#6B6B6B] hover:text-[#0E0E0E] inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={14} /> Back to admin
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#0E0E0E]">Pitch kit</h1>
          <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl">
            Ready-made talking points for the four audiences you'll meet most often.
            Tap copy to use in a chat or email, or download as a text file to print and
            bring to a meeting.
          </p>
        </div>
        <Button
          onClick={downloadEverything}
          className="bg-[#D4AF37] hover:bg-[#C9A227] text-[#0E0E0E] rounded-full font-semibold"
          data-testid="pitch-download-all"
        >
          <DownloadSimple size={15} className="mr-1.5" weight="bold" /> Download everything
        </Button>
      </div>

      <Tabs value={active} onValueChange={setActive} className="w-full">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6 bg-transparent p-0 h-auto">
          {Object.entries(PITCH_CONTENT).map(([key, data]) => {
            const Icon = data.icon;
            return (
              <TabsTrigger
                key={key}
                value={key}
                data-testid={`pitch-tab-${key}`}
                className="rounded-full border border-[#EAEAEA] data-[state=active]:bg-[#0E0E0E] data-[state=active]:text-white data-[state=active]:border-[#0E0E0E] py-2.5 inline-flex items-center gap-2 text-xs sm:text-sm"
              >
                <Icon size={14} weight="duotone" /> {data.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(PITCH_CONTENT).map(([key, data]) => (
          <TabsContent key={key} value={key} className="mt-0">
            <div className="border border-[#EAEAEA] rounded-2xl bg-white overflow-hidden" data-testid={`pitch-content-${key}`}>
              <div className="px-5 sm:px-7 py-5 border-b border-[#EAEAEA] bg-[#FAFAFA] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="font-serif text-xl text-[#0E0E0E]">{data.label}</h2>
                  <p className="text-xs text-[#6B6B6B] mt-1 max-w-xl">{data.blurb}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    onClick={() => copyAll(key)}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                    data-testid={`pitch-copy-${key}`}
                  >
                    <Copy size={13} className="mr-1.5" /> Copy all
                  </Button>
                  <Button
                    onClick={() => downloadAll(key)}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                    data-testid={`pitch-download-${key}`}
                  >
                    <DownloadSimple size={13} className="mr-1.5" /> Download
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-[#EAEAEA]">
                {data.sections.map((s, idx) => (
                  <PitchSection key={idx} section={s} sectionId={`${key}-${idx}`} />
                ))}
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <p className="text-[11px] text-[#9A9A9A] mt-6 text-center max-w-2xl mx-auto">
        Edit the copy by updating <code className="bg-[#FAFAFA] px-1 rounded">PITCH_CONTENT</code> in
        <code className="bg-[#FAFAFA] px-1 rounded">frontend/src/pages/AdminPitchKit.jsx</code>.
        Changes apply on the next deploy.
      </p>
    </div>
  );
}

function PitchSection({ section, sectionId }) {
  const [copied, setCopied] = useState(false);
  const copyOne = () => {
    navigator.clipboard.writeText(section.text).then(() => {
      setCopied(true);
      toast.success("Copied this section.");
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="px-5 sm:px-7 py-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-serif text-base text-[#0E0E0E] flex-1">{section.heading}</h3>
        <button
          type="button"
          onClick={copyOne}
          data-testid={`pitch-copy-section-${sectionId}`}
          className="inline-flex items-center gap-1 text-[11px] text-[#6B6B6B] hover:text-[#0E0E0E] flex-shrink-0"
        >
          {copied ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>
      <p className="text-sm text-[#3F3F3F] leading-relaxed whitespace-pre-line">{section.text}</p>
    </div>
  );
}
